/**
 * Use Unlink Document Hook
 *
 * Wraps the unlinkDocumentFromChecklistItem mutation using useApiMutation
 * for consistent error handling. Blocks unlink when answers reference
 * the document and shows a descriptive toast.
 */

import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import type { Id } from "@/packages/types";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export function useUnlinkDocument() {
  const { t: tCommon } = useTranslation("common");
  const { t: tAudits } = useTranslation("audits");

  const mutation = useApiMutation(
    api.checklist_mutations.unlinkDocumentFromChecklistItem,
    {
      name: "unlink-document-from-checklist-item",
      successMessage: tAudits("documents.successUnlink"),
      onError: (error) => {
        if (
          error.details?.code === "ANSWERS_REFERENCE_DOCUMENT" &&
          typeof error.details?.affectedCount === "number"
        ) {
          toast.error(
            tCommon("errors.answersReferenceDocument", {
              count: error.details.affectedCount,
            }),
          );
          return false; // Prevent default toast
        }
        // Return undefined → default toast for other errors
      },
    },
  );

  return (args: {
    documentId: Id<"documents">;
    checklistItemId: Id<"checklistItems">;
  }) => mutation.mutateAsync(args);
}
